import { useEffect, useMemo, useState } from "react";
import type { Video } from "@/types/video";
import VideoCard from "@/components/feature/VideoCard";
import EmptyState from "@/components/base/EmptyState";
import Pagination from "@/components/base/Pagination";
import FilterPanel from "@/components/feature/FilterPanel";
import {
  applyFilters,
  defaultFilter,
  sortVideos,
  SORT_OPTIONS,
  type VideoFilterState,
} from "@/utils/videoFilters";

const PAGE_SIZE = 8;

interface VideoBrowserProps {
  videos: Video[];
  lockedFilter?: Partial<VideoFilterState>;
  showCategory?: boolean;
  showTag?: boolean;
  emptyTitle?: string;
  emptyDescription?: string;
}

export default function VideoBrowser({
  videos,
  lockedFilter,
  showCategory = true,
  showTag = true,
  emptyTitle = "没有找到符合条件的视频",
  emptyDescription = "试着放宽筛选条件，或点击重置按钮查看全部视频内容。",
}: VideoBrowserProps) {
  const [filter, setFilter] = useState<VideoFilterState>(defaultFilter);
  const [sort, setSort] = useState("latest");
  const [page, setPage] = useState(1);
  const [mode, setMode] = useState<"paged" | "load">("paged");
  const [panelOpen, setPanelOpen] = useState(false);

  const effective: VideoFilterState = useMemo(
    () => ({ ...filter, ...lockedFilter }),
    [filter, lockedFilter],
  );

  const result = useMemo(() => sortVideos(applyFilters(videos, effective), sort), [videos, effective, sort]);

  const totalPages = Math.max(1, Math.ceil(result.length / PAGE_SIZE));
  const displayed =
    mode === "load"
      ? result.slice(0, page * PAGE_SIZE)
      : result.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const hasMore = mode === "load" && page * PAGE_SIZE < result.length;

  useEffect(() => {
    setPage(1);
  }, [effective.category, effective.tag, effective.region, effective.date, effective.duration, sort]);

  const update = (patch: Partial<VideoFilterState>) => setFilter((prev) => ({ ...prev, ...patch }));
  const reset = () => setFilter(defaultFilter);

  const activeCount = [filter.category, filter.tag, filter.region, filter.date, filter.duration].filter(
    (value) => value !== "all",
  ).length;

  const filterContent = (
    <FilterPanel
      filter={filter}
      onChange={update}
      onReset={reset}
      showCategory={showCategory}
      showTag={showTag}
      activeCount={activeCount}
    />
  );

  return (
    <div className="flex flex-col gap-6 lg:flex-row">
      <aside className="hidden w-64 shrink-0 lg:block">
        <div className="sticky top-20 rounded-lg border border-background-200 bg-background-50 p-5">{filterContent}</div>
      </aside>

      <div className="min-w-0 flex-1">
        <div className="mb-5 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-background-200 bg-background-50 px-4 py-3">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setPanelOpen(true)}
              className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 px-3 py-1.5 text-sm text-foreground-700 transition-colors duration-200 hover:border-primary-300 hover:text-primary-600 lg:hidden"
            >
              <i className="ri-filter-3-line"></i>
              筛选
              {activeCount > 0 ? (
                <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-primary-500 px-1 text-xs text-background-50">
                  {activeCount}
                </span>
              ) : null}
            </button>
            <p className="text-sm text-foreground-500">
              共 <span className="font-semibold text-primary-600">{result.length}</span> 个视频
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-1 rounded-full border border-background-300 p-1">
              <button
                type="button"
                onClick={() => {
                  setMode("paged");
                  setPage(1);
                }}
                className={`cursor-pointer whitespace-nowrap rounded-full px-3 py-1 text-xs font-medium transition-colors duration-200 ${
                  mode === "paged" ? "bg-primary-500 text-background-50" : "text-foreground-600 hover:text-primary-600"
                }`}
              >
                分页
              </button>
              <button
                type="button"
                onClick={() => {
                  setMode("load");
                  setPage(1);
                }}
                className={`cursor-pointer whitespace-nowrap rounded-full px-3 py-1 text-xs font-medium transition-colors duration-200 ${
                  mode === "load" ? "bg-primary-500 text-background-50" : "text-foreground-600 hover:text-primary-600"
                }`}
              >
                加载更多
              </button>
            </div>

            <div className="relative">
              <select
                value={sort}
                onChange={(event) => setSort(event.target.value)}
                aria-label="排序方式"
                className="cursor-pointer appearance-none rounded-md border border-background-300 bg-background-50 py-1.5 pl-3 pr-8 text-sm text-foreground-700 outline-none transition-colors duration-200 hover:border-primary-300 focus:border-primary-400"
              >
                {SORT_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <i className="ri-arrow-down-s-line pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-base text-foreground-500"></i>
            </div>
          </div>
        </div>

        {result.length === 0 ? (
          <EmptyState title={emptyTitle} description={emptyDescription} actionLabel="重置筛选条件" onAction={reset} />
        ) : (
          <>
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-3">
              {displayed.map((video) => (
                <VideoCard key={video.id} video={video} />
              ))}
            </div>

            <div className="mt-8">
              {mode === "paged" ? (
                <Pagination currentPage={page} totalPages={totalPages} onChange={setPage} />
              ) : hasMore ? (
                <div className="text-center">
                  <button
                    type="button"
                    onClick={() => setPage((prev) => prev + 1)}
                    className="cursor-pointer whitespace-nowrap rounded-md border border-primary-300 bg-primary-50 px-6 py-2.5 text-sm font-medium text-primary-700 transition-colors duration-200 hover:bg-primary-100"
                  >
                    加载更多视频
                  </button>
                </div>
              ) : (
                <p className="text-center text-xs text-foreground-400">已经加载全部 {result.length} 个视频</p>
              )}
            </div>
          </>
        )}
      </div>

      {panelOpen ? (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/40" onClick={() => setPanelOpen(false)}></div>
          <div className="absolute right-0 top-0 flex h-full w-[85%] max-w-sm flex-col overflow-y-auto bg-background-50 p-5">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="font-heading text-base font-bold text-foreground-950">筛选条件</h3>
              <button
                type="button"
                onClick={() => setPanelOpen(false)}
                aria-label="关闭筛选"
                className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-md text-foreground-600 hover:bg-background-100"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
            <div className="flex-1">{filterContent}</div>
            <button
              type="button"
              onClick={() => setPanelOpen(false)}
              className="mt-5 cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors duration-200 hover:bg-primary-600"
            >
              查看 {result.length} 个结果
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}